<template>
	<div class="stage">
		<page-header :title="template.id ? `Edit Template: ${template.name}` : 'New Provisioning Template'" />

		<div class="mb-6 flex justify-end">
			<button class="jmiBtn red mr-3" @click="cancel">Cancel</button>
			<button class="jmiBtn blue" @click="save">Save</button>
		</div>

		<div class="grid grid-cols-1 gap-6 md:grid-cols-3">
			<div class="rounded-lg bg-white p-5 shadow dark:bg-offmaindark md:col-span-1">
				<h2 class="mb-5 text-lg font-medium dark:text-white">Template Details</h2>

				<div class="mb-5">
					<label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
					<input
						v-model="template.name"
						type="text"
						class="w-full rounded-md border border-gray-300 p-3 dark:border-gray-700 dark:bg-gray-800"
						placeholder="Template Name"
					/>
				</div>

				<div class="mb-5">
					<label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Type</label>
					<input
						v-model="template.type"
						type="text"
						class="w-full rounded-md border border-gray-300 p-3 dark:border-gray-700 dark:bg-gray-800"
						placeholder="Template Type (e.g. Yealink, Cisco, etc.)"
					/>
				</div>

				<div class="mb-5">
					<label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
					<textarea
						v-model="template.description"
						class="w-full rounded-md border border-gray-300 p-3 dark:border-gray-700 dark:bg-gray-800"
						placeholder="Template Description"
						rows="5"
					></textarea>
				</div>

				<div v-if="template.id" class="text-sm text-gray-500 dark:text-gray-400">
					<p>Created: {{ $global.dateFormat(template.createdAt) }}</p>
					<p>Last Updated: {{ $global.dateFormat(template.updatedAt) }}</p>
				</div>
			</div>

			<div class="rounded-lg bg-white p-5 shadow dark:bg-offmaindark md:col-span-2">
				<h2 class="mb-5 text-lg font-medium dark:text-white">Template Content</h2>

				<div class="h-[700px] rounded-md border border-gray-300 dark:border-gray-700">
					<v-ace-editor
						ref="editor"
						v-model:value="template.content"
						lang="xml"
						:theme="isDarkMode ? 'github_dark' : 'chrome'"
						style="height: 100%; width: 100%"
						:options="{
							fontSize: '14px',
							showPrintMargin: false,
							showGutter: true,
							highlightActiveLine: true,
							enableBasicAutocompletion: true,
							enableLiveAutocompletion: true,
							enableSnippets: true,
							showLineNumbers: true,
							tabSize: 2,
							wrap: true
						}"
					/>
				</div>

				<div class="mt-3 text-sm text-gray-500 dark:text-gray-400">
					<p>
						Use variables like <code class="rounded bg-gray-100 px-1 dark:bg-gray-800">{mac}</code>,
						<code class="rounded bg-gray-100 px-1 dark:bg-gray-800">{tenant}</code>, etc. in your template.
					</p>
				</div>
			</div>
		</div>

		<div class="mt-6 flex justify-end">
			<button class="jmiBtn red mr-3 px-6 py-3 text-base" @click="cancel">Cancel</button>
			<button class="jmiBtn blue px-6 py-3 text-base" @click="save">Save</button>
		</div>
	</div>
</template>

<script>
import { router } from '../router'
import deviceTemplatesApi from '../scripts/devicetemplates/devicetemplates.api'
import { VAceEditor } from 'vue3-ace-editor'
import 'ace-builds/src-noconflict/mode-xml'
import 'ace-builds/src-noconflict/mode-json'
import 'ace-builds/src-noconflict/theme-chrome'
import 'ace-builds/src-noconflict/theme-github_dark'

export default {
	components: {
		VAceEditor
	},
	data() {
		return {
			template: {
				name: '',
				type: '',
				description: '',
				content: ''
			},
			loading: false
		}
	},
	computed: {
		isDarkMode() {
			return document.documentElement.classList.contains('dark')
		}
	},
	watch: {
		isDarkMode(newVal) {
			if (this.$refs.editor) {
				this.$nextTick(() => {
					this.$refs.editor.setTheme(newVal ? 'github_dark' : 'chrome')
				})
			}
		}
	},
	created() {
		const templateId = this.$route.params.id

		if (templateId && templateId !== 'new') {
			this.loading = true
			deviceTemplatesApi
				.getById(templateId)
				.then((response) => {
					console.log('Template loaded:', response.data)
					this.template = response.data

					if (!this.template.content) {
						this.template.content = ''
					}
				})
				.catch((error) => {
					console.error('Error loading template:', error)
					this.$swal({
						title: 'Error',
						text: 'Failed to load template',
						icon: 'error',
						confirmButtonText: 'OK'
					}).then(() => {
						router.push('/devices?tab=templates')
					})
				})
				.finally(() => {
					this.loading = false
				})
		} else {
			this.template.content = ''
		}
	},
	methods: {
		async save() {
			if (!this.template.name) {
				this.$swal({
					title: 'Error',
					text: 'Template name is required',
					icon: 'error',
					confirmButtonText: 'OK'
				})
				return
			}

			try {
				this.loading = true
				let response

				if (this.template.id) {
					response = await deviceTemplatesApi.update(this.template)
				} else {
					response = await deviceTemplatesApi.create(this.template)
				}

				this.$swal({
					title: 'Success',
					text: `Template ${this.template.id ? 'updated' : 'created'} successfully`,
					icon: 'success',
					confirmButtonText: 'OK'
				}).then(() => {
					router.push('/devices?tab=templates')
				})
			} catch (error) {
				console.error('Error saving template:', error)
				this.$swal({
					title: 'Error',
					text: 'Failed to save template',
					icon: 'error',
					confirmButtonText: 'OK'
				})
			} finally {
				this.loading = false
			}
		},
		cancel() {
			router.push('/devices?tab=templates')
		}
	}
}
</script>
